import { useState, useRef, useEffect, useCallback } from "react";
import { Bot, ChevronDown, Loader2, Search, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function AIFundingInput({
  minChars = 100,
  maxChars = 1200,
  isLoading = false,
  onStart,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [exampleOpen, setExampleOpen] = useState(false);
  const textareaRef = useRef(null);

  const countNonSpaceChars = useCallback((text) => {
    return (text || "").replace(/\s/g, "").length;
  }, []);

  const nonSpaceCount = countNonSpaceChars(query);
  const isValid = nonSpaceCount >= minChars && nonSpaceCount <= maxChars;
  const canSubmit = isValid && !isLoading;

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(92, el.scrollHeight)}px`;
  }, []);

  useEffect(() => {
    autoResize();
  }, [query, autoResize]);

  const parseAmount = (value) => {
    if (value === "" || value == null) return null;
    const text = String(value).trim().toLowerCase();
    const multiplier = text.endsWith("k") ? 1_000 : text.endsWith("m") ? 1_000_000 : text.endsWith("b") ? 1_000_000_000 : 1;
    const num = parseFloat(text.replace(/[^0-9.]/g, ""));
    if (Number.isNaN(num)) return null;
    return Math.round(num * multiplier);
  };

  const handleStart = useCallback(() => {
    if (!canSubmit) return;
    onStart?.({
      description: query,
      minAmount: parseAmount(minAmount),
      maxAmount: parseAmount(maxAmount),
    });
  }, [canSubmit, onStart, query, minAmount, maxAmount]);

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex shrink-0 items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
            <Bot className="h-3.5 w-3.5" />
            {t("app:AiFunding.badge")}
          </div>
          <h1 className="!m-0 !text-xl font-semibold leading-tight tracking-tight text-stone-950 md:!text-2xl">
            {t("app:AiFunding.title")}
          </h1>
        </div>
        <p className="max-w-4xl text-sm leading-6 text-stone-600">
          {t("app:AiFunding.description")}
        </p>
      </div>

      <div className="mt-4 grid gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-stone-400" />
          <textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("app:AiFunding.placeholder", { min: minChars, max: maxChars })}
            rows={3}
            className="min-h-[92px] w-full resize-none rounded-lg border border-stone-200 bg-stone-50/70 py-3 pl-10 pr-3 text-sm leading-6 text-stone-900 shadow-sm outline-none transition-colors placeholder:text-stone-400 focus:border-stone-300 focus:bg-white focus:ring-4 focus:ring-stone-100"
          />
        </div>

        <div className="rounded-lg border border-stone-200 bg-stone-50/70">
          <button
            type="button"
            onClick={() => setExampleOpen((value) => !value)}
            aria-expanded={exampleOpen}
            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs font-medium text-stone-700 hover:text-stone-950"
          >
            <span>{t("app:AiFunding.exampleTrigger")}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-stone-400 transition-transform ${exampleOpen ? "rotate-180" : ""}`}
            />
          </button>
          {exampleOpen ? (
            <div className="border-t border-stone-200 px-3 py-3">
              <p className="text-xs leading-5 text-stone-500">
                {t("app:AiFunding.exampleIntro")}
              </p>
              <div className="mt-2 rounded-md border border-stone-200 bg-white p-3 text-sm leading-6 text-stone-700">
                {t("app:AiFunding.exampleText")}
              </div>
            </div>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-[minmax(0,180px)_minmax(0,180px)_auto_1fr] md:items-end">
          <label className="block">
            <span className="text-xs font-medium text-stone-600">{t("app:AiFunding.amountFrom")}</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder={t("app:AiFunding.amountFromPlaceholder")}
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-stone-200 bg-white px-3 text-sm outline-none transition-colors focus:border-stone-300 focus:ring-4 focus:ring-stone-100"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-stone-600">{t("app:AiFunding.amountTo")}</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder={t("app:AiFunding.amountToPlaceholder")}
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-stone-200 bg-white px-3 text-sm outline-none transition-colors focus:border-stone-300 focus:ring-4 focus:ring-stone-100"
            />
          </label>
          <button
            type="button"
            onClick={handleStart}
            disabled={!canSubmit}
            className={
              "cursor-pointer inline-flex h-9 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium shadow-sm transition-colors " +
              (canSubmit
                ? "bg-stone-900 text-white hover:bg-stone-800"
                : "cursor-not-allowed bg-stone-200 text-stone-500")
            }
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {isLoading ? t("app:AiFunding.running") : t("app:AiFunding.start")}
          </button>
        </div>

        <div className="flex flex-col gap-2 text-xs text-stone-500 sm:flex-row sm:items-center sm:justify-between">
          <span className={isValid ? "text-stone-500" : "text-rose-600"}>
            {nonSpaceCount < minChars &&
              t("app:AiFunding.helperTooShort", { needed: minChars - nonSpaceCount })}
            {nonSpaceCount > maxChars &&
              t("app:AiFunding.helperTooLong", { overflow: nonSpaceCount - maxChars, max: maxChars })}
            {isValid && t("app:AiFunding.helperValid")}
          </span>
          <span className="tabular-nums">
            {t("app:AiFunding.counter", { count: nonSpaceCount, min: minChars, max: maxChars })}
          </span>
        </div>
      </div>
    </section>
  );
}
